import { CollectDataStepStatus, CollectDataStepType } from 'src/constants/collect-data/enums'
import { US_STATE_OPTIONS } from 'src/constants/oatfi-business/states'
import {
  DotNumberOption,
  PartnerDriverDeletionReason,
  PartnerDriversTableFields,
  StatusTone,
  StepAction,
  StepsTableFields,
  VehicleType,
} from './enums'

/** Retool's `stepStatusMap` wording. The prototype's "Not started" relabel is not used. */
export const stepStatusTitles: Record<CollectDataStepStatus, string> = {
  [CollectDataStepStatus.Created]: 'Created',
  [CollectDataStepStatus.InProgress]: 'In progress',
  [CollectDataStepStatus.WaitingForInternalApproval]: 'Waiting for internal approval',
  [CollectDataStepStatus.WaitingForApproval]: 'Waiting for approval',
  [CollectDataStepStatus.Completed]: 'Completed',
  [CollectDataStepStatus.CompletedNotActive]: 'Completed, not active',
  [CollectDataStepStatus.Failed]: 'Failed',
  [CollectDataStepStatus.Archived]: 'Archived',
}

export const stepStatusTones: Record<CollectDataStepStatus, StatusTone> = {
  [CollectDataStepStatus.Created]: StatusTone.Neutral,
  [CollectDataStepStatus.InProgress]: StatusTone.Info,
  [CollectDataStepStatus.WaitingForInternalApproval]: StatusTone.Warning,
  [CollectDataStepStatus.WaitingForApproval]: StatusTone.Warning,
  [CollectDataStepStatus.Completed]: StatusTone.Success,
  [CollectDataStepStatus.CompletedNotActive]: StatusTone.Success,
  [CollectDataStepStatus.Failed]: StatusTone.Critical,
  [CollectDataStepStatus.Archived]: StatusTone.Neutral,
}

export const vehicleTypeTitles: Record<VehicleType, string> = {
  [VehicleType.Sedan]: 'Sedan',
  [VehicleType.Hatchback]: 'Hatchback',
  [VehicleType.StationWagon]: 'Station Wagon',
  [VehicleType.Suv]: 'SUV',
  [VehicleType.Truck]: 'Truck',
  [VehicleType.Bicycle]: 'Bicycle',
  [VehicleType.EBike]: 'E-Bike',
  [VehicleType.EScooter]: 'E-Scooter',
  [VehicleType.Motorcycle]: 'Motorcycle',
  [VehicleType.Scooter]: 'Scooter',
  [VehicleType.Van]: 'Van',
  [VehicleType.CargoVan]: 'Cargo Van',
  [VehicleType.BoxTruck]: 'Box Truck',
}

/**
 * Vehicle types for which year, make, model and VIN are optional on complete-vehicle-and-insurance.
 * Motorcycle is NOT in this list — the backend requires all four for it.
 */
export const MICRO_MOBILITY_VEHICLE_TYPES: VehicleType[] = [
  VehicleType.Bicycle,
  VehicleType.EBike,
  VehicleType.EScooter,
  VehicleType.Scooter,
]

export const stepActionTitles: Record<StepAction, string> = {
  [StepAction.CompleteBackgroundCheck]: 'Complete background check',
  [StepAction.CompleteVehicleInsurance]: 'Complete vehicle & insurance',
  [StepAction.CompleteDriverLicense]: 'Complete driver license',
  [StepAction.CompleteLmsTraining]: 'Complete LMS training',
  [StepAction.CompleteInsuranceDeclaration]: 'Complete insurance declaration',
  [StepAction.CompleteVehicleRegistration]: 'Complete vehicle registration',
  [StepAction.CompleteDotNumber]: 'Complete DOT number',
  [StepAction.CompleteFailed]: 'Complete failed step',
  [StepAction.UploadDocuments]: 'Upload documents',
  [StepAction.Reset]: 'Reset step',
  [StepAction.DeleteDocuments]: 'Delete all documents',
}

/** Rendered in the critical colour in the row actions menu. */
export const DESTRUCTIVE_STEP_ACTIONS: StepAction[] = [StepAction.Reset, StepAction.DeleteDocuments]

/** Step types `POST /steps/:stepId/document` accepts; any other type is a 400. */
export const UPLOADABLE_STEP_TYPES: CollectDataStepType[] = [
  CollectDataStepType.DriverLicense,
  CollectDataStepType.BGC,
  CollectDataStepType.VehicleInsurance,
  CollectDataStepType.EIN,
  CollectDataStepType.VehicleRegistration,
  CollectDataStepType.SignContract,
  CollectDataStepType.InsuranceDeclaration,
  CollectDataStepType.LMSTraining,
]

export const TERMINAL_STEP_STATUSES: CollectDataStepStatus[] = [
  CollectDataStepStatus.Completed,
  CollectDataStepStatus.CompletedNotActive,
  CollectDataStepStatus.Archived,
]

// Multer limits on the backend routes; one more file is a 400.
export const STEP_DOCUMENT_MAX_FILES = 2
export const BACKGROUND_CHECK_MAX_FILES = 2
export const INSURANCE_DECLARATION_MAX_FILES = 5
export const VEHICLE_REGISTRATION_MAX_FILES = 1

export const dotNumberOptionTitles: Record<DotNumberOption, string> = {
  [DotNumberOption.Dot]: 'Submit DOT number',
  [DotNumberOption.UsDot]: 'Submit USDOT number',
  [DotNumberOption.NoNumber]: "Don't have a DOT/USDOT number",
}

/**
 * The client app's DOT form offers the US states, DC and Puerto Rico (its `constants/states.ts`);
 * the shared admin list has no Puerto Rico, so it is added here rather than in the shared file.
 */
export const DOT_STATE_OF_ISSUE_OPTIONS = US_STATE_OPTIONS.flatMap((option) => {
  if (option.value === 'PA') {
    return [option, { value: 'PR', label: 'Puerto Rico' }]
  }

  return [option]
})

export const partnerDriverDeletionReasonTitles: Record<PartnerDriverDeletionReason, string> = {
  [PartnerDriverDeletionReason.RequestedDataDeletion]: 'Requested data deletion',
  [PartnerDriverDeletionReason.RejectedDeactivatedForFraud]: 'Rejected deactivated for fraud',
}

/** `decimal(4,2)` column — the DTO's `@Max(100)` would accept 100, the column would not. */
export const REIMBURSEMENT_PERCENTAGE_MAX = 99.99

/** Required by `GET /:partnerDriverId/steps` and ignored by it — both values return the same steps. */
export const STEPS_SOURCE = 'onboarding'

export const partnerDriverTableHeaderTitles: Record<PartnerDriversTableFields, string> = {
  [PartnerDriversTableFields.name]: 'Name',
  [PartnerDriversTableFields.email]: 'Email',
  [PartnerDriversTableFields.phone]: 'Phone',
  [PartnerDriversTableFields.partnerName]: 'Partner',
  [PartnerDriversTableFields.isActive]: 'Status',
  [PartnerDriversTableFields.reimbursementAmount]: 'Reimbursement $',
  [PartnerDriversTableFields.reimbursementPercentage]: 'Reimbursement %',
  [PartnerDriversTableFields.pendingChecks]: 'Pending checks',
  [PartnerDriversTableFields.otpCode]: 'OTP code',
  [PartnerDriversTableFields.collectDataTemplateId]: 'Template ID',
  [PartnerDriversTableFields.id]: 'Driver ID',
}

export const stepsTableHeaderTitles: Record<StepsTableFields, string> = {
  [StepsTableFields.step]: 'Step',
  [StepsTableFields.status]: 'Status',
  [StepsTableFields.documents]: 'Documents',
  [StepsTableFields.details]: 'Details',
  [StepsTableFields.actions]: '',
}

// The search has no pagination server-side; matches are a handful of rows in practice.
export const partnerDriverPageSizeOptions = [25, 50, 100]

export const PARTNER_DRIVERS_GRID_COLUMNS_STORAGE_KEY = 'admin.partnerDrivers.gridColumns'
